import { ReactNode, useEffect, useRef, useState } from 'react';

import { useAuth } from '@/hooks/useAuth';
import { log } from '@/utils/functions/logger/logger';
import { computeQpsScore } from '@/utils/functions/qps/computeQpsScore';

import { EMPTY_QPS_STATE, QpsActionsContext, QpsStateContext } from './QpsContext';

import type {
  QpsActions,
  QpsContextQuestion,
  QpsContextQuestionnaire,
  QpsContextState,
  QpsUserAnswers,
} from '@/types/qps';

const QPS_STORAGE_KEY = 'qps_context';

const isStoredQuestion = (value: unknown): boolean => {
  const question = value as QpsContextQuestion | null;
  return (
    typeof question === 'object' &&
    question !== null &&
    typeof question.id === 'string' &&
    typeof question.label === 'string' &&
    Array.isArray(question.notes) &&
    Array.isArray(question.answers) &&
    question.answers.every(
      (answer) =>
        typeof answer === 'object' &&
        answer !== null &&
        typeof answer.value === 'string' &&
        typeof answer.isCorrect === 'boolean'
    ) &&
    (question.userRespond === undefined || typeof question.userRespond === 'string')
  );
};

const isStoredState = (value: unknown): value is QpsContextState => {
  const state = value as QpsContextState | null;
  return (
    typeof state === 'object' &&
    state !== null &&
    typeof state.arbitrageId === 'string' &&
    Array.isArray(state.questionnaires) &&
    state.questionnaires.every(
      (questionnaire) =>
        typeof questionnaire === 'object' &&
        questionnaire !== null &&
        typeof questionnaire.codeSupport === 'string' &&
        typeof questionnaire.version === 'string' &&
        Array.isArray(questionnaire.questions) &&
        questionnaire.questions.every(isStoredQuestion) &&
        typeof questionnaire.currentQuestionIndex === 'number' &&
        typeof questionnaire.isPassed === 'boolean'
    )
  );
};

// localStorage can be unavailable (private browsing, blocked site data): the context then lives in
// memory only, it never throws.
const readStoredState = (): QpsContextState => {
  try {
    const stored = globalThis.localStorage.getItem(QPS_STORAGE_KEY);
    const parsed: unknown = stored === null ? null : JSON.parse(stored);
    return isStoredState(parsed) ? parsed : EMPTY_QPS_STATE;
  } catch (error) {
    log.warn('[qps] Lecture impossible', error);
    return EMPTY_QPS_STATE;
  }
};

const writeStoredState = (state: QpsContextState) => {
  try {
    if (state.arbitrageId === null) {
      globalThis.localStorage.removeItem(QPS_STORAGE_KEY);
    } else {
      globalThis.localStorage.setItem(QPS_STORAGE_KEY, JSON.stringify(state));
    }
  } catch (error) {
    log.warn('[qps] Écriture impossible', error);
  }
};

// First unanswered question, the last one once everything is answered.
const getResumeIndex = (questions: QpsContextQuestion[]): number => {
  const index = questions.findIndex((question) => question.userRespond === undefined);
  return index === -1 ? Math.max(0, questions.length - 1) : index;
};

/**
 * Questionnaires of the arbitrage being signed, with the client's answers and current question.
 * Saved under a single localStorage key, so the client resumes where they left off. It only holds
 * one arbitrage: another arbitrage starts from scratch. Emptied once the signature succeeded and on
 * logout. The questionnaires come from the arbitrage detail, through `initQuestionnaires`.
 */
const QpsProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [state, setState] = useState<QpsContextState>(readStoredState);
  const stateRef = useRef(state);

  const commit = (next: QpsContextState) => {
    stateRef.current = next;
    setState(next);
    writeStoredState(next);
  };

  const updateQuestionnaire = (
    codeSupport: string,
    update: (questionnaire: QpsContextQuestionnaire) => QpsContextQuestionnaire
  ) => {
    commit({
      ...stateRef.current,
      questionnaires: stateRef.current.questionnaires.map((questionnaire) =>
        questionnaire.codeSupport === codeSupport ? update(questionnaire) : questionnaire
      ),
    });
  };

  const actionsRef = useRef<QpsActions>({
    initQuestionnaires: (arbitrageId, entries) => {
      const kept = stateRef.current.arbitrageId === arbitrageId ? stateRef.current.questionnaires : [];
      const questionnaires = entries.map(({ codeSupport, questionnaire }) => {
        const existing = kept.find((candidate) => candidate.codeSupport === codeSupport);
        if (existing?.version !== questionnaire.version) {
          return {
            codeSupport,
            code: questionnaire.code,
            version: questionnaire.version,
            questions: questionnaire.questions,
            currentQuestionIndex: 0,
            isPassed: false,
          };
        }
        // The model comes from the API (wording, notes, right answers): only the answers still
        // possible and the passed flag are kept.
        const questions = questionnaire.questions.map((question) => {
          const stored = existing.questions.find((candidate) => candidate.id === question.id)?.userRespond;
          const isStillPossible = question.answers.some((answer) => answer.value === stored);
          return { ...question, userRespond: isStillPossible ? stored : undefined };
        });
        return {
          codeSupport,
          code: questionnaire.code,
          version: questionnaire.version,
          questions,
          currentQuestionIndex: getResumeIndex(questions),
          isPassed: existing.isPassed,
        };
      });
      commit({ arbitrageId, questionnaires });
      return questionnaires;
    },

    // An answer is final: a question already answered keeps its first answer.
    answerQuestion: (codeSupport, value) =>
      updateQuestionnaire(codeSupport, (questionnaire) => {
        const { currentQuestionIndex } = questionnaire;
        if (questionnaire.questions[currentQuestionIndex]?.userRespond !== undefined) return questionnaire;
        return {
          ...questionnaire,
          questions: questionnaire.questions.map((question, index) =>
            index === currentQuestionIndex ? { ...question, userRespond: value } : question
          ),
        };
      }),

    setQuestionIndex: (codeSupport, index) =>
      updateQuestionnaire(codeSupport, (questionnaire) => ({ ...questionnaire, currentQuestionIndex: index })),

    finishQuestionnaire: (codeSupport) => {
      const questionnaire = stateRef.current.questionnaires.find((candidate) => candidate.codeSupport === codeSupport);
      if (!questionnaire) return null;
      const userAnswers: QpsUserAnswers = {};
      for (const { id, userRespond } of questionnaire.questions) {
        if (userRespond !== undefined) userAnswers[id] = userRespond;
      }
      const result = computeQpsScore(questionnaire, userAnswers);
      updateQuestionnaire(codeSupport, (current) => ({
        ...current,
        questions: current.questions.map((question) => ({ ...question, userRespond: undefined })),
        currentQuestionIndex: 0,
        isPassed: current.isPassed || result.isPassed,
      }));
      return result;
    },

    clearQps: () => commit(EMPTY_QPS_STATE),
  });

  // Logout (any path: menu, demo exit, expired session): the next client must not find the answers.
  // A first render without user (login page) is not a logout and keeps the saved questionnaires.
  const previousUserRef = useRef(user);
  useEffect(() => {
    if (previousUserRef.current && !user) actionsRef.current.clearQps();
    previousUserRef.current = user;
  }, [user]);

  return (
    <QpsStateContext.Provider value={state}>
      <QpsActionsContext.Provider value={actionsRef.current}>{children}</QpsActionsContext.Provider>
    </QpsStateContext.Provider>
  );
};

export default QpsProvider;
export { QPS_STORAGE_KEY };
